import * as Sentry from "@sentry/react";
import classNames from "classnames";
import { useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import {
  useIsLoading,
  useRequestAfterEverySiteLoad,
  useRequestError,
} from "shared/hooks/requestStatusHooks";
import { selectFeatureFlag } from "shared/reducers/sysReducer";

import { listCases } from "app/actions/caseActions";
import { useAppSelector } from "app/hooks";
import { useSelectedLabStation } from "hooks/useSelectedLabStation";
import AppError from "ui/components/AppError";
import SlideBarcode from "ui/components/barcodes/slide";
import Spinner from "ui/components/Spinner";
import { UNEVEN_PRINT_CASES_LIST } from "util/sentryEvents";

const BatchPrintPage = () => {
  const { case_ids } = useParams();
  const caseIds = [
    ...new Set(
      case_ids
        ?.split(",")
        ?.map((id) => Number(id.trim()))
        .filter((v) => v && !Number.isNaN(v)) || []
    ),
  ];
  const singlePage = useAppSelector((state) =>
    selectFeatureFlag(state, "singlePageBarcodePrinting")
  );

  const [searchParams] = useSearchParams();

  const selectedLabStation = useSelectedLabStation();
  const casesReady = useRequestAfterEverySiteLoad(listCases, {
    parentLookupLabStationId: selectedLabStation.id,
    id__in: caseIds.join(","),
  });
  const listCasesLoading = useIsLoading(listCases, {
    parentLookupLabStationId: selectedLabStation.id,
  });
  const listCasesError = useRequestError(listCases, {
    parentLookupLabStationId: selectedLabStation.id,
  });
  const casesState = useAppSelector((state) => state.casesWithWork);

  const printer = searchParams.get("printer");

  const printCases = caseIds?.map((id) => casesState[id]).filter((v) => v);
  if (!listCasesLoading && printCases?.length !== caseIds?.length) {
    Sentry.captureEvent({
      event_id: UNEVEN_PRINT_CASES_LIST,
      message: `Printing cases ${caseIds?.join(
        ", "
      )} may not have been available in printCases ${printCases
        ?.map((c) => c.id)
        .join(", ")}`,
    });
  }

  const slideIds =
    searchParams
      .get("slides")
      ?.split(",")
      .map((id) => Number(id.trim()))
      .filter((v) => v && !Number.isNaN(v)) || [];

  useEffect(() => {
    if (window.opener) {
      const closeWindow = () => {
        window.close();
      };
      window.addEventListener("afterprint", closeWindow);

      return () => {
        window.removeEventListener("afterprint", closeWindow);
      };
    }
  }, []);

  useEffect(() => {
    if (window.opener && casesReady && printCases) {
      const print = () => {
        setTimeout(() => window.print(), 250);
      };
      if (document.readyState === "complete") {
        print();
      } else {
        window.addEventListener("load", print);
        return () => window.removeEventListener("load", print);
      }
    }
  }, [casesReady, printCases]);

  if (listCasesLoading) return <Spinner />;

  if (listCasesError) return <AppError />;

  if (!printCases) return null;

  return (
    <div className={classNames("Print", { SinglePagePrint: singlePage })}>
      {slideIds.map((slide_id) => {
        const printCase = printCases.find((c) =>
          c.slides.find((s) => s.id === slide_id)
        );
        return printCase ? (
          <SlideBarcode
            key={`slide-${slide_id}`}
            id={slide_id}
            printCase={printCase}
            printer={printer}
          />
        ) : null;
      })}
    </div>
  );
};

export default BatchPrintPage;
